import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { AbsenceBadge } from '@/components/AbsenceBadge';
import { Modal, Notice } from '@/components/Modal';
import { EmptyCard, LoadingBlock } from '@/components/PageHeader';
import { Avatar } from '@/components/PersonCard';
import { directoryPhotoUrl } from '@/lib/directory/useDirectory';
import { ORG_CARD_SHADOW, elbowPath } from '@/lib/orgChartGeometry';
import {
  formatKrw,
  slotsOfPreset,
  useOrgLayout,
  useSaveOrgLayout,
  type OrgCandidate,
  type OrgGroup,
  type OrgLayout,
  type OrgSlot,
  type OrgTenure,
  type PresetOption,
} from '@/pages/hr/useOrgLayout';

/**
 * 현장 조직도 «판» — 사용자가 넣어 둔 기본 양식 그대로 (docs/design/조직도 · V379).
 *
 * <h2>왜 격자인가</h2>
 * 양식의 정본은 엑셀 한 장이고, 거기서 <b>어느 칸에 있는가</b>가 곧 조직의 모양이다. 트리로
 * 옮기면 총괄소장·품질·안전처럼 선이 닿지 않는 묶음을 둘 자리가 없어진다 — 실제 양식이 그렇다.
 * 회의실 배치도({@code RoomFloorMap})가 같은 판단을 이미 내렸다: 자리는 좌표로 두고 내용만 잇는다.
 *
 * <h2>색을 쓰지 않는다</h2>
 * 원본 엑셀의 파란 칸·흰 칸은 <b>서식일 뿐 뜻이 없다</b>(사용자 확인 2026-10-01). 그대로 옮기면
 * 보는 사람이 거기서 상주·공석 같은 뜻을 읽어 낸다 — 아무도 그런 뜻을 넣은 적이 없는데.
 * 색이 갈리는 것은 <b>사람이 서 있는가</b> 하나뿐이다.
 *
 * <h2>고치는 자리는 하나다 (사용자 지시 2026-10-01)</h2>
 * 판을 고치는 곳은 <b>인사관리 › 인력배치표 › 조직도</b> 뿐이고, 현장 조회의 조직도 탭은 읽기만
 * 한다({@code readOnly}). 두 화면에서 같은 판을 고칠 수 있으면 서로의 저장을 덮어쓰게 되고,
 * 현장 탭은 애초에 «이 현장이 어떻게 생겼나» 를 보러 오는 자리다. 그 현장의 현장대리인만 현장 탭에서
 * 고치고, 고친 판은 저장 대신 변경 요청으로 올라간다(V452 — 인사배치 담당이 승인하면 바뀐다).
 *
 * <h2>제목은 입력칸으로 그리지 않는다</h2>
 * 입력칸은 넘치는 글자를 <b>줄바꿈 대신 잘라낸다.</b> 여섯 칸짜리 격자에서 「기계 / 전기 / 통신」
 * 이 중간에서 끊겼고, 화면만 봐서는 이름이 그런 줄 알게 된다 — 글자는 글자로 그리고, 고칠 때만
 * 입력칸으로 바꿔 끼운다.
 *
 * <h2>생김새는 본사 트리와 같다 (사용자 지시 2026-10-01)</h2>
 * 둥근 카드·옅은 그림자·뿌리는 진한 머리칸·둥글게 꺾이는 선 — {@code OrgTreeChart} 와 같은 값을 쓴다
 * ({@code lib/orgChartGeometry}). 자리(격자)는 양식이 정하고, 옷만 같이 입는다.
 */

const CELL_MIN_HEIGHT = 32;

/** 줄과 줄 사이의 빈 띠. 가로줄이 이 안을 지나므로 좁히면 선이 칸에 닿는다. */
const BUS_GAP = 40;

type Props = {
  siteId: string | null;
  /** 현장 조회의 조직도 탭이 쓰는 모양. 고치는 손이 하나도 서지 않는다. */
  readOnly?: boolean;
  /**
   * 서버의 판 대신 그릴 판 — 현장대리인의 초안이나 대기 중인 변경 요청(V452). 주면 고친 것을 저장하지
   * 않고 {@code onGroupsChange} 로 넘긴다.
   */
  groups?: OrgGroup[];
  onGroupsChange?: (groups: OrgGroup[]) => void;
  /** 종결 현장의 떠난 사람과 「발령 · 전출」 까지 — 인력배치표 › 조직도만(사용자 지시 2026-10-16). */
  former?: boolean;
};

export function OrgChartBoard({ siteId, readOnly = false, groups, onGroupsChange, former = false }: Props) {
  const layout = useOrgLayout(siteId, former);
  const save = useSaveOrgLayout(siteId, former);

  const data = layout.data;

  /**
   * 바뀐 판을 보낸다. <b>화면에 꽂는 일과 실패했을 때 되돌리는 일은 저장 훅이 함께 들고 있다</b>
   * ({@link useSaveOrgLayout}) — 꽂는 쪽과 되돌리는 쪽이 떨어져 있으면 되돌리는 쪽만 빠뜨린다.
   */
  const apply = useCallback((groups: OrgGroup[]) => save.mutate(groups), [save]);

  if (!siteId) return <EmptyCard>현장이 없습니다.</EmptyCard>;
  if (layout.isPending) return <LoadingBlock />;
  if (layout.error || !data) return <Notice tone="error">조직도를 불러오지 못했습니다.</Notice>;

  return (
    <section className="card p-4 space-y-3">
      {/* 저장이 막혔을 때만 말한다. 「저장했습니다」 는 적지 않는다 — 누른 결과가 화면에 이미 서 있다. */}
      {save.error && <Notice tone="error">저장하지 못했습니다. 권한이 없거나 연결이 끊겼습니다.</Notice>}

      <Board
        layout={groups ? { ...data, groups } : data}
        readOnly={readOnly}
        onChange={onGroupsChange ?? apply}
      />
    </section>
  );
}

// ── 판 ───────────────────────────────────────────────────────────────────────

/** 묶음 한 칸의 열쇠. 묶음은 화면에서 만들지 않으므로 늘 서버가 준 id 다. */
function keyOf(group: OrgGroup): string {
  return group.id ?? `${group.gridCol}:${group.gridRow}`;
}

function Board({
  layout,
  readOnly,
  onChange,
}: {
  layout: OrgLayout;
  readOnly: boolean;
  onChange: (groups: OrgGroup[]) => void;
}) {
  const { columns, groups } = layout;
  const [editingSlot, setEditingSlot] = useState<{ groupKey: string; slotIndex: number } | null>(null);

  const rows = Math.max(1, ...groups.map((group) => group.gridRow));

  const occupied = useMemo(() => {
    const map = new Map<string, OrgGroup>();
    for (const group of groups) map.set(`${group.gridCol}:${group.gridRow}`, group);
    return map;
  }, [groups]);

  const replace = (key: string, next: OrgGroup) =>
    onChange(groups.map((group) => (keyOf(group) === key ? next : group)));

  const cells: React.ReactNode[] = [];
  for (let row = 1; row <= rows; row += 1) {
    for (let col = 1; col <= columns; col += 1) {
      const group = occupied.get(`${col}:${row}`);
      cells.push(
        group ? (
          <GroupBox
            key={`g-${keyOf(group)}`}
            group={group}
            layout={layout}
            readOnly={readOnly}
            onChange={(next) => replace(keyOf(group), next)}
            onPickSlot={(slotIndex) => setEditingSlot({ groupKey: keyOf(group), slotIndex })}
          />
        ) : (
          <div key={`e-${col}-${row}`} style={{ gridColumn: col, gridRow: row }} />
        ),
      );
    }
  }

  const editing = editingSlot
    ? groups.find((group) => keyOf(group) === editingSlot.groupKey) ?? null
    : null;

  /** 그 사람을 그 자리에 세운다. 미배치 줄에서 고른 결과가 여기로 온다. */
  const seat = (member: OrgCandidate, groupKey: string, slotIndex: number) =>
    onChange(
      groups.map((group) =>
        keyOf(group) !== groupKey
          ? group
          : {
              ...group,
              slots: group.slots.map((slot, index) =>
                index === slotIndex
                  ? { ...slot, personnelId: member.personnelId, personName: member.name, personRank: member.rank, hasPhoto: false }
                  : slot,
              ),
            },
      ),
    );

  return (
    <>
      <Unplaced layout={layout} readOnly={readOnly} onSeat={seat} />

      <Connectors groups={groups} columns={columns} rows={rows}>
        {cells}
      </Connectors>

      {editing && editingSlot && (
        <SlotEditor
          group={editing}
          slotIndex={editingSlot.slotIndex}
          layout={layout}
          onClose={() => setEditingSlot(null)}
          onChange={(next) => {
            replace(editingSlot.groupKey, next);
            setEditingSlot(null);
          }}
        />
      )}
    </>
  );
}

// ── 미배치 ───────────────────────────────────────────────────────────────────

/**
 * <b>배치는 됐는데 아직 자리에 서지 않은 사람</b> — 그리고 <b>세우는 손</b>
 * (사용자 지시 2026-10-01).
 *
 * <h2>왜 판 위인가</h2>
 * 조직도는 자리에 세워야 보인다. 전술판에서 배치해도 누군가 자리를 눌러 세우기 전까지 판 위에
 * 없고, 그동안 화면 어디에도 빠졌다는 표시가 없었다 — 조직도를 보고 인원을 세면 그 사람이
 * 사라진다. 실제로 하노이에서 안전 파견 한 명이 그렇게 빠져 있었다(안전 구간을 안 골라 자리가
 * 없었다). 판 <b>아래</b>에 달았더니 화면 밖으로 밀려 보이지 않았다.
 *
 * <h2>보여 주기만 하지 않는다</h2>
 * 이름을 누르면 빈 자리를 골라 <b>그 자리에 세운다</b>. 「빠진 사람이 있다」 고 적어 두고 할 수 있는
 * 일을 주지 않으면, 읽은 사람은 전술판으로 갔다가 다시 돌아와야 한다.
 *
 * <p>이름을 빨갛게 칠하지 않는다 — 사람 이름의 빨강은 <b>그 사람이 잘못한 것</b>처럼 읽힌다.
 * 빠진 것은 자리이지 사람이 아니다.
 */
function Unplaced({
  layout,
  readOnly,
  onSeat,
}: {
  layout: OrgLayout;
  readOnly: boolean;
  onSeat: (member: OrgCandidate, groupKey: string, slotIndex: number) => void;
}) {
  const [seating, setSeating] = useState<OrgCandidate | null>(null);

  const left = useMemo(() => {
    const standing = new Set<string>();
    for (const group of layout.groups) {
      for (const slot of group.slots) {
        if (slot.personnelId) standing.add(slot.personnelId);
      }
    }
    return layout.members.filter((member) => !standing.has(member.personnelId));
  }, [layout.groups, layout.members]);

  if (left.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap" style={{ marginBottom: '4px' }}>
      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>미배치</span>

      {left.map((member) => {
        const label = `${member.name ?? ''}${member.rank ? ` ${member.rank}` : ''}`;
        const style: React.CSSProperties = {
          fontSize: '12px',
          padding: '3px 10px',
          borderRadius: '999px',
          border: '1px solid var(--border)',
          background: 'var(--bg-card)',
          color: 'var(--text-primary)',
        };
        return readOnly ? (
          <span key={member.personnelId} style={style}>{label}</span>
        ) : (
          <button
            key={member.personnelId}
            type="button"
            title="자리에 세우기"
            onClick={() => setSeating(member)}
            style={{ ...style, cursor: 'pointer' }}
          >
            {label}
          </button>
        );
      })}

      {seating && (
        <SeatPicker
          member={seating}
          layout={layout}
          onClose={() => setSeating(null)}
          onPick={(groupKey, slotIndex) => {
            onSeat(seating, groupKey, slotIndex);
            setSeating(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * 어느 자리에 세울까 — <b>비어 있는 자리만</b> 고르게 한다.
 *
 * <p>찬 자리를 함께 늘어놓으면 모르고 눌러 서 있던 사람을 밀어낸다. 사람을 바꾸는 일은 그 자리를
 * 눌러서 하는 일이고({@code SlotEditor}), 여기는 «빈 자리에 채우는» 자리다.
 *
 * <p>자리가 하나도 비어 있지 않을 수 있다 — 품질·안전이 등급·구간을 안 골라 자리 자체가 없는
 * 경우다. 그때는 그렇게 적는다. 자리를 여기서 만들지는 않는다.
 */
function SeatPicker({
  member,
  layout,
  onClose,
  onPick,
}: {
  member: OrgCandidate;
  layout: OrgLayout;
  onClose: () => void;
  onPick: (groupKey: string, slotIndex: number) => void;
}) {
  const open = layout.groups.flatMap((group) =>
    group.slots
      .map((slot, index) => ({ group, slot, index }))
      .filter((row) => !row.slot.personnelId),
  );

  return (
    // 「을(를)」 를 쓰지 않는다 — 받침에 따라 달라지는 조사를 괄호로 때우면 화면이 번역투가 된다.
    <Modal title={`자리 고르기 — ${member.name ?? ''}${member.rank ? ` ${member.rank}` : ''}`} onClose={onClose}>
      {open.length === 0 ? (
        <EmptyCard>빈 자리가 없습니다. 품질·안전은 등급과 구간을 고르면 자리가 생깁니다.</EmptyCard>
      ) : (
        <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
          {open.map(({ group, slot, index }) => (
            <button
              key={`${keyOf(group)}-${index}`}
              type="button"
              onClick={() => onPick(keyOf(group), index)}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
                width: '100%',
                textAlign: 'left',
                padding: '7px 8px',
                borderBottom: '1px solid var(--border)',
              }}
            >
              <strong style={{ fontSize: '13px' }}>{slot.title}</strong>
              <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{group.title}</span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── 격자 + 선 ────────────────────────────────────────────────────────────────

/**
 * 묶음끼리의 선. <b>격자가 아니라 실제 칸의 좌표</b>에서 그린다 — 칸 높이는 자리 줄 수에 따라
 * 제각각이라(안전 묶음은 다섯 줄까지 간다), 줄 번호로 계산한 선은 늘 어딘가에서 어긋난다.
 */
function Connectors({
  groups,
  columns,
  rows,
  children,
}: {
  groups: OrgGroup[];
  columns: number;
  rows: number;
  children: React.ReactNode;
}) {
  const boardRef = useRef<HTMLDivElement>(null);
  const [paths, setPaths] = useState<string[]>([]);
  const [size, setSize] = useState({ width: 0, height: 0 });

  const measure = useCallback(() => {
    const board = boardRef.current;
    if (!board) return;
    const base = board.getBoundingClientRect();
    setSize({ width: base.width, height: base.height });

    /*
      현장소장 칸이 비고 현장대리인 칸에 사람이 서 있으면 선은 대리인에서 내려온다 — 그 현장의 장이
      소장 먼저, 없으면 대리인이라서다(2026-10-28). 저장된 선(parentId)은 소장 그대로라 소장을 세우면 돌아간다.
    */
    const head = groups.find((item) => item.title === SITE_HEAD_GROUP);
    const agent = groups.find((item) => item.title === SITE_AGENT_GROUP);
    const leadFallback = head && agent && head.id
      && !head.slots.some((slot) => slot.personnelId)
      && agent.slots.some((slot) => slot.personnelId)
      ? { from: head.id, to: keyOf(agent) }
      : null;

    const next: string[] = [];
    for (const group of groups) {
      const parentKey = leadFallback && group.parentId === leadFallback.from ? leadFallback.to : group.parentId;
      if (!parentKey) continue;
      const child = board.querySelector<HTMLElement>(`[data-org-key="${cssEscape(keyOf(group))}"]`);
      const parent = board.querySelector<HTMLElement>(`[data-org-key="${cssEscape(parentKey)}"]`);
      if (!child || !parent) continue;

      const c = child.getBoundingClientRect();
      const p = parent.getBoundingClientRect();
      const x1 = p.left - base.left + p.width / 2;
      const y1 = p.bottom - base.top;
      const x2 = c.left - base.left + c.width / 2;
      const y2 = c.top - base.top;
      /*
        가로줄은 <b>자식 바로 위</b>에 둔다. 한때 부모와 자식의 가운데(y1+(y2-y1)/2)에 뒀는데,
        현장소장(첫 줄)에서 셋째 줄로 내려오는 줄의 «가운데» 가 하필 품질·안전 칸의 한복판이라
        가로줄이 「품질관리자 (중급)」 글자를 뚫고 지나갔다(사용자 지적 2026-10-01).
        자식 바로 위는 어느 줄에서 와도 <b>칸과 칸 사이의 빈 띠</b>라 아무것도 가리지 않는다.
      */
      const bus = Math.max(y1, y2 - BUS_GAP / 2);
      next.push(elbowPath(x1, y1, x2, y2, bus));
    }
    setPaths(next);
  }, [groups]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    const board = boardRef.current;
    if (!board || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(board);
    // 칸 하나가 길어져도 선이 따라가야 한다 — 판만 보면 안쪽 변화를 놓친다.
    board.querySelectorAll('[data-org-key]').forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [measure]);

  return (
    <div ref={boardRef} style={{ position: 'relative' }}>
      <svg
        width={size.width}
        height={size.height}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}
        aria-hidden
      >
        {paths.map((path) => (
          <path key={path} d={path} fill="none" stroke="var(--border-strong)" strokeWidth={1.5} strokeLinecap="round" />
        ))}
      </svg>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${rows}, minmax(${CELL_MIN_HEIGHT}px, auto))`,
          gap: `${BUS_GAP}px 16px`,
          alignItems: 'start',
          padding: '8px 4px 12px',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** `CSS.escape` 가 없는 환경(앱 웹뷰)도 있다. 열쇠는 uuid 아니면 우리가 만든 글자라 이걸로 충분하다. */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}

// ── 묶음 한 칸 ───────────────────────────────────────────────────────────────

/**
 * 묶음 한 칸 — <b>틀은 고정이고 사람만 드나든다</b> (사용자 지시 2026-10-01).
 *
 * <p>한때 머리칸을 끌어 자리를 바꾸고, 묶음을 더하고 지우고, 이름까지 고칠 수 있었다. 그러나
 * 배치표에서 사람이 하는 일은 <b>「이 자리에 누구를 세울까」</b> 하나다 — 조직의 생김새는
 * 양식이 정한 것이고, 그것을 매번 고칠 수 있게 두면 현장마다 판이 달라져 조직도가 양식이기를
 * 그만둔다. 그래서 끌기·추가·삭제·이름 고치기를 전부 걷어냈다.
 *
 * <p>남은 손은 셋이다: 자리를 눌러 <b>사람을 세우고 비우기</b>, 자리 <b>한 줄 더하고 빼기</b>
 * (현장마다 공사 인원이 다르다), 품질·안전의 <b>등급·구간 고르기</b>.
 */
function GroupBox({
  group,
  layout,
  readOnly,
  onChange,
  onPickSlot,
}: {
  group: OrgGroup;
  layout: OrgLayout;
  readOnly: boolean;
  onChange: (next: OrgGroup) => void;
  onPickSlot: (slotIndex: number) => void;
}) {
  const presets =
    group.kind === 'QUALITY' ? layout.qualityPresets : group.kind === 'SAFETY' ? layout.safetyPresets : null;
  const tenures = useMemo(
    () => new Map((layout.tenures ?? []).map((tenure) => [tenure.personnelId, tenure])),
    [layout.tenures],
  );

  /**
   * 등급·구간을 고치면 그 묶음의 자리 줄을 다시 깐다.
   *
   * <h3>이름이 같은 자리의 사람은 데려간다</h3>
   * 「고급품질대상」에서 「특급품질대상」으로 바꿔도 <b>초급·중급에 선 사람은 그대로</b> 남는다.
   * 한때 줄을 통째로 비웠는데, 한 칸 잘못 눌렀다가 세워 둔 세 사람이 말없이 날아갔다.
   *
   * <h3>빠지는 사람이 있으면 묻는다</h3>
   * 자격이 다른 자리로 사람을 밀어 올리지는 않는다(「고급」에 섰던 사람을 「특급」으로 옮기면
   * 자격이 바뀐 것처럼 보인다). 그래서 사라지는 자리의 사람은 빠지고, <b>빠질 사람이 있을 때만</b>
   * 확인을 받는다.
   */
  const choosePreset = (value: string) => {
    const titles = slotsOfPreset(presets ?? [], value || null);
    const standing = group.slots.filter((slot) => slot.personnelId);
    const carried = new Set<number>();

    const slots: OrgSlot[] = titles.map((title, index) => {
      const hit = standing.findIndex((slot, at) => !carried.has(at) && slot.title === title);
      const kept = hit < 0 ? undefined : standing[hit];
      if (!kept) {
        return { id: null, title, sortOrder: index, personnelId: null, personName: null, personRank: null, hasPhoto: false };
      }
      carried.add(hit);
      return {
        id: null,
        title,
        sortOrder: index,
        personnelId: kept.personnelId,
        personName: kept.personName,
        personRank: kept.personRank,
        hasPhoto: kept.hasPhoto,
      };
    });

    const dropped = standing.filter((_, at) => !carried.has(at));
    if (dropped.length > 0) {
      const names = dropped.map((slot) => `${slot.personName ?? '?'}(${slot.title})`).join(', ');
      if (!window.confirm(`${names} 이(가) 자리에서 빠집니다. 바꿀까요?`)) return;
    }

    onChange({ ...group, preset: value || null, slots });
  };

  // 선이 내려오지 않는 묶음(총괄소장·현장소장·현장대리인)이 뿌리 — 본사 트리의 맨 위 칸과 같은 옷을 입는다.
  const root = !group.parentId;

  return (
    <div
      data-org-key={keyOf(group)}
      style={{
        gridColumn: group.gridCol,
        gridRow: group.gridRow,
        borderRadius: '10px',
        border: `1px solid ${root ? 'var(--accent)' : 'var(--border)'}`,
        background: 'var(--bg-card)',
        boxShadow: ORG_CARD_SHADOW,
        overflow: 'hidden',
      }}
    >
      {/* 머리칸 — 양식이 정한 이름이라 여기서 고치지 않는다. */}
      <div
        style={{
          background: root ? 'var(--accent)' : 'var(--accent-bg)',
          color: root ? '#fff' : 'var(--accent)',
          padding: '8px 10px',
          minHeight: `${CELL_MIN_HEIGHT}px`,
          fontSize: '13px',
          fontWeight: 700,
          textAlign: 'center',
          lineHeight: 1.25,
          // 좁은 칸의 「기계 / 전기 / 통신」 은 잘리지 않고 접힌다.
          whiteSpace: 'normal',
          wordBreak: 'keep-all',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {group.title}
      </div>

      {presets && !readOnly && (
        <PresetPicker group={group} presets={presets} layout={layout} onChoose={choosePreset} />
      )}

      {group.slots.map((slot, index) => (
        <SlotRow
          key={slot.id ?? `${index}-${slot.title}`}
          slot={slot}
          tenure={slot.personnelId ? tenures.get(slot.personnelId) : undefined}
          readOnly={readOnly}
          onClick={() => onPickSlot(index)}
        />
      ))}

      {!readOnly && (
        <button
          type="button"
          onClick={() =>
            onChange({
              ...group,
              slots: [
                ...group.slots,
                {
                  id: null,
                  title: '새 자리',
                  sortOrder: group.slots.length,
                  personnelId: null,
                  personName: null,
                  personRank: null,
                  hasPhoto: false,
                },
              ],
            })
          }
          /*
            점선 네모로 그리던 때가 있었는데, 묶음마다 하나씩 여덟이면 판 아래가 점선으로 들쭉날쭉해
            양식보다 먼저 읽혔다(사용자 지적 2026-10-01). 표의 마지막 줄처럼 <b>같은 테두리</b>로
            이어 붙이고 글자만 옅게 둔다.
          */
          className="no-print org-tree-member"
          style={{
            display: 'block',
            width: '100%',
            fontSize: '12px',
            lineHeight: 1,
            color: 'var(--text-muted)',
            padding: '5px 0 7px',
          }}
        >
          +
        </button>
      )}
    </div>
  );
}

/**
 * 품질등급 · 안전구간 드롭박스. <b>고칠 수 있는 화면에만 선다</b> — 읽는 화면에서 드롭박스는
 * 눌러도 아무 일이 없는 칸이고, 그 칸이 안전 묶음을 한 줄씩 더 길게 만든다.
 *
 * <p>안전은 옆에 <b>계약금액</b>을 적는다. 구간이 금액에서 나온 값이라, 숫자를 보지 못하면 세 명이
 * 맞는지 네 명이 맞는지 화면만 보고는 알 수 없다. 금액을 읽지 못한 현장은 구간을 고르지 않은 채
 * 두고 사람이 고른다 — 「기본 안전1인」 으로 깔면 1500억 현장이 조용히 한 명으로 선다.
 */
function PresetPicker({
  group,
  presets,
  layout,
  onChoose,
}: {
  group: OrgGroup;
  presets: PresetOption[];
  layout: OrgLayout;
  onChoose: (value: string) => void;
}) {
  const amount = group.kind === 'SAFETY' ? formatKrw(layout.contractAmountKrw) : null;

  return (
    // 인쇄에는 드롭박스가 서지 않는다 — 종이에 눌리지 않는 칸을 찍어 봐야 양식만 어지럽다.
    <div className="no-print" style={{ padding: '6px 6px 2px' }}>
      <select
        className="input-field"
        style={{ width: '100%', fontSize: '11px', padding: '3px 4px' }}
        value={group.preset ?? ''}
        onChange={(event) => onChoose(event.target.value)}
      >
        <option value="">미지정</option>
        {presets.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {group.kind === 'SAFETY' && (
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', textAlign: 'center' }}>
          {amount ?? '공사금액 미상'}
        </div>
      )}
    </div>
  );
}

/** 2024-03-04 → 24.03.04 */
function shortDate(iso: string): string {
  return iso.slice(2, 10).replaceAll('-', '.');
}

/** 자리 한 줄. 사람이 서 있으면 이름이, 비어 있으면 자리 이름이 옅게 선다. */
function SlotRow({
  slot,
  tenure,
  readOnly,
  onClick,
}: {
  slot: OrgSlot;
  /** 종결 현장에서만 온다. 단서가 없는 쪽은 적지 않는다. */
  tenure?: OrgTenure;
  readOnly: boolean;
  onClick: () => void;
}) {
  const filled = !!slot.personnelId;

  const style: React.CSSProperties = {
    display: 'block',
    width: '100%',
    minHeight: `${CELL_MIN_HEIGHT}px`,
    margin: '2px 0',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '12px',
    textAlign: 'center',
    lineHeight: 1.3,
    // 좁은 칸에 「품질관리자 (초급)」 이 들어간다. 잘라내는 대신 접는다 — 자리 이름은 자격이라
    // 뒷말이 잘리면 다른 자리가 된다.
    whiteSpace: 'normal',
    wordBreak: 'keep-all',
    background: 'transparent',
    color: filled ? 'var(--text-primary)' : 'var(--text-muted)',
  };

  // 자리 이름은 옅게 위, 사람은 굵게 아래 — 본사 트리의 사람 줄(사진 · 이름 굵게 · 직급 옅게)과 같은 결.
  // 사진도 같은 길({@code directoryPhotoUrl})로 부른다 (사용자 지적 2026-10-02 「배치돼 있는데 사진이 안 보이네」).
  const body = (
    <>
      <span style={{ display: 'block', fontSize: '11px', color: 'var(--text-muted)' }}>{slot.title}</span>
      {filled && slot.personnelId && (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px', maxWidth: '100%', fontWeight: 700 }}>
          <Avatar
            name={slot.personName ?? ''}
            size={18}
            photoUrl={slot.hasPhoto ? directoryPhotoUrl(slot.personnelId) : null}
          />
          <span style={{ minWidth: 0 }}>
            {slot.personName}
            {slot.personRank && (
              <span style={{ fontWeight: 400, fontSize: '11px', color: 'var(--text-muted)' }}> {slot.personRank}</span>
            )}
          </span>
          <AbsenceBadge personnelId={slot.personnelId} />
        </span>
      )}
      {filled && tenure && (tenure.joinedOn || tenure.leftOn) && (
        <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)' }}>
          {[tenure.joinedOn && `발령 ${shortDate(tenure.joinedOn)}`, tenure.leftOn && `전출 ${shortDate(tenure.leftOn)}`]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )}
    </>
  );

  const label = `${slot.title}${filled ? ` · ${slot.personName ?? ''}` : ' · 공석'}`;

  // 읽는 화면에서는 단추로 그리지 않는다 — 눌러도 아무 일이 없는 단추는 «고장난 화면» 으로 읽힌다.
  return readOnly ? (
    <div title={label} style={style}>
      {body}
    </div>
  ) : (
    <button type="button" onClick={onClick} title={label} className="org-tree-member" style={style}>
      {body}
    </button>
  );
}

// ── 자리 고치기 ──────────────────────────────────────────────────────────────

/** 연락처 공개 버튼이 서는 묶음과, 기본이 꺼짐인 자리 (V418 · 서버 {@code SiteOrgLayoutService} 와 같은 글자). */
const ADMIN_GROUP = '공무';
const ASSISTANT_SLOT = '사무보조';
/** 현장의 장 묶음 — 소장이 비면 선이 대리인에서 내려온다({@link Connectors}). */
const SITE_HEAD_GROUP = '현장소장';
const SITE_AGENT_GROUP = '현장대리인';

/**
 * 자리 한 줄을 고치는 창 — <b>사람·이름·삭제</b>가 한자리에 있다.
 *
 * <p>후보는 이 현장에 배치된 사람뿐이다. 여기서 사람을 세워도 그 사람의 소속은 바뀌지 않는다 —
 * 소속을 옮기는 일은 전술판에만 있고, 두 곳에서 고칠 수 있으면 언젠가 한 곳만 고쳐진다.
 */
function SlotEditor({
  group,
  slotIndex,
  layout,
  onClose,
  onChange,
}: {
  group: OrgGroup;
  slotIndex: number;
  layout: OrgLayout;
  onClose: () => void;
  onChange: (next: OrgGroup) => void;
}) {
  const slot = group.slots[slotIndex];
  const [title, setTitle] = useState(slot?.title ?? '');
  const [query, setQuery] = useState('');

  /** 이 판의 다른 자리에 이미 서 있는 사람. 막지는 않는다 — 겸하는 자리가 실제로 있다. */
  const placed = useMemo(() => {
    const map = new Map<string, string>();
    for (const other of layout.groups) {
      for (const [index, item] of other.slots.entries()) {
        if (!item.personnelId) continue;
        if (keyOf(other) === keyOf(group) && index === slotIndex) continue;
        map.set(item.personnelId, `${other.title} · ${item.title}`);
      }
    }
    return map;
  }, [layout.groups, group, slotIndex]);

  /**
   * 후보 — 걸러내고, <b>담당이 이 묶음인 사람을 맨 위로</b> 올린다.
   *
   * <p>배치의 담당은 자유 입력이라 「공무」가 실제 자료에서는 「공무담당」·「공무 담당」·「공무팀장」
   * 으로 적혀 있다(2026-10-01 실측). 그래서 완전일치만 보지 않고 <b>글자가 들어 있는지</b>까지
   * 본다 — 완전일치가 먼저고 그다음이 부분일치다. 거르지는 않는다: 담당이 뭐라 적혀 있든
   * 그 자리에 세울 수 있어야 한다.
   */
  const candidates = useMemo(() => {
    const text = query.trim();
    const found = text
      ? layout.members.filter((member) =>
          [member.name, member.rank, member.roleTitle, member.fieldName]
            .filter(Boolean)
            .some((value) => (value as string).includes(text)),
        )
      : layout.members;

    const rank = (member: OrgCandidate): number => {
      const duty = member.roleTitle ?? '';
      if (!duty) return 2;
      if (duty === group.title) return 0;
      return duty.includes(group.title) || group.title.includes(duty) ? 1 : 2;
    };
    return [...found].sort((a, b) => rank(a) - rank(b));
  }, [layout.members, query, group.title]);

  if (!slot) return null;

  const commit = (personnelId: string | null, person?: OrgCandidate) =>
    onChange({
      ...group,
      slots: group.slots.map((item, index) =>
        index === slotIndex
          ? {
              ...item,
              title: title.trim() || item.title,
              personnelId,
              personName: person?.name ?? null,
              personRank: person?.rank ?? null,
              // 사진 유무는 서버가 안다 — 저장 응답이 바꿔 꽂는다.
              hasPhoto: false,
            }
          : item,
      ),
    });

  return (
    <Modal
      title={`${group.title} · ${slot.title}`}
      onClose={onClose}
      actions={
        <>
          <button
            className="btn-secondary"
            style={{ color: 'var(--danger)' }}
            /*
              서 있는 사람이 있으면 <b>이름을 적어</b> 묻는다. 「자리를 지울까요」 만으로는 그
              자리에 누가 서 있었는지 보이지 않아, 사람까지 함께 지우는 줄 모르고 누른다.
            */
            onClick={() => {
              const who = slot.personName ? `${slot.personName} 님이 선 ` : '';
              if (!window.confirm(`${who}「${slot.title}」 자리를 지울까요?`)) return;
              onChange({ ...group, slots: group.slots.filter((_, index) => index !== slotIndex) });
            }}
          >
            자리 삭제
          </button>
          {/*
            공무 자리의 [연락처 공개] (사용자 지시 2026-10-07 · V418). 현장 개요·현장현황의 공무
            연락처가 이 값을 본다. 현장대리인은 늘 공개이고 다른 묶음은 연락처에 오르지 않아 공무에만 선다.
            저장 전 줄은 값이 없다 — 서버 기본(사무보조만 꺼짐)과 같은 답으로 읽는다.
          */}
          {group.title === ADMIN_GROUP && (() => {
            const shown = slot.contactPublic ?? slot.title !== ASSISTANT_SLOT;
            return (
              <button
                className="btn-secondary"
                aria-pressed={shown}
                onClick={() =>
                  onChange({
                    ...group,
                    slots: group.slots.map((item, index) =>
                      index === slotIndex ? { ...item, contactPublic: !shown } : item,
                    ),
                  })
                }
              >
                {shown ? '☑' : '☐'} 연락처 공개
              </button>
            );
          })()}
          <button className="btn-secondary" onClick={() => commit(null)}>
            비우기
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="flex items-center gap-2">
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', width: '56px' }}>자리 이름</span>
          <input
            className="input-field"
            style={{ flex: 1, fontSize: '13px' }}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>

        <input
          className="input-field"
          style={{ width: '100%', fontSize: '13px' }}
          placeholder="이름 · 직급 · 담당으로 찾기"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />

        {layout.members.length === 0 ? (
          <EmptyCard>이 현장에 배치된 사람이 없습니다.</EmptyCard>
        ) : (
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            {candidates.map((member) => {
              const here = member.personnelId === slot.personnelId;
              const elsewhere = placed.get(member.personnelId);
              return (
                <button
                  key={member.personnelId}
                  type="button"
                  onClick={() => commit(member.personnelId, member)}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '6px',
                    width: '100%',
                    textAlign: 'left',
                    padding: '6px 8px',
                    borderBottom: '1px solid var(--border)',
                    background: here ? 'var(--bg-hover)' : 'transparent',
                  }}
                >
                  <strong style={{ fontSize: '13px' }}>{member.name}</strong>
                  {member.rank && (
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{member.rank}</span>
                  )}
                  {member.roleTitle && (
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{member.roleTitle}</span>
                  )}
                  {elsewhere && (
                    <span className="ml-auto" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      {elsewhere}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
